import styles from './numberField.module.css'
import { useEffect, useRef, useState } from 'react'
import { isDefined, toNumber, round as roundFn, roundUp, roundDown } from '../../../../../utils/typeUtils';

interface Props extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
    id: string
    inputKey?: string
    value?: number
    defaultValue?: number
    min?: number
    max?: number
    integer?: boolean
    round?: boolean
    optional?: boolean
    fit?: boolean
    focus?: boolean
    thousandsSeparator?: boolean
    className?: string
    onChange?: (value?: number, manualChange?: boolean) => void
}

const formatNumber = (val: number): string =>
    new Intl.NumberFormat('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(val)

// Dots are always thousands separators (visual). Only comma is decimal.
const formatWhileTyping = (raw: string): string => {
    const commaIdx = raw.indexOf(',')
    if (commaIdx !== -1) {
        const intDigits = raw.substring(0, commaIdx).replace(/[^0-9]/g, '')
        const decPart = raw.substring(commaIdx + 1).replace(/[^0-9]/g, '').substring(0, 2)
        const intFormatted = intDigits ? intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, '.') : ''
        return intFormatted + ',' + decPart
    }
    const intDigits = raw.replace(/[^0-9]/g, '')
    return intDigits ? intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, '.') : ''
}

const getNewCursorPos = (raw: string, formatted: string, rawCursorPos: number): number => {
    const commaIdx = raw.indexOf(',')
    let sigBeforeCursor = 0
    for (let i = 0; i < rawCursorPos && i < raw.length; i++) {
        if (/[0-9]/.test(raw[i]) || i === commaIdx) sigBeforeCursor++
    }
    let sig = 0
    for (let i = 0; i < formatted.length; i++) {
        if (sig === sigBeforeCursor) return i
        if (/[0-9]/.test(formatted[i]) || formatted[i] === ',') sig++
    }
    return formatted.length
}

export const NumberField = (props: Props) => {
    const { id, inputKey, value, defaultValue, integer, min, max, round = true, optional, fit, focus, thousandsSeparator = false, className, onChange, ...rest } = props
    const [stateValue, setStateValue] = useState(defaultValue)
    const inputRef = useRef<HTMLInputElement>(null)
    const fmt = formatNumber
    const initialNum = isDefined(value) ? value : defaultValue
    const [displayText, setDisplayText] = useState<string>(
        thousandsSeparator && isDefined(initialNum) ? fmt(initialNum!) : ''
    )
    const isFocusedRef = useRef(false)
    const currentValueRef = useRef<number | undefined>(initialNum)
    const prevDisplayRef = useRef<string>(
        thousandsSeparator && isDefined(initialNum) ? fmt(initialNum!) : ''
    )

    useEffect(() => {
        if (isDefined(value) && value !== stateValue) {
            setValue(value)
            if (thousandsSeparator && !isFocusedRef.current) {
                const formatted = fmt(value!)
                setDisplayText(formatted)
                prevDisplayRef.current = formatted
            }
        }
    }, [value])

    useEffect(() => {
        focus && inputRef && inputRef.current?.focus()
    }, [focus])

    const setValue = (newValue?: number, manualChange = false) => {
        currentValueRef.current = newValue
        setStateValue(newValue)
        onChange && onChange(newValue, manualChange)
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = toNumber(e.target?.value)
        setValue(newValue, true)
    }

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value
        const cursorPos = e.target.selectionStart ?? raw.length
        // Convert a typed '.' to ',' only when a single char was added — paste uses handlePaste instead
        let processedRaw = raw
        if (raw.length === prevDisplayRef.current.length + 1 && raw[cursorPos - 1] === '.') {
            processedRaw = raw.substring(0, cursorPos - 1) + ',' + raw.substring(cursorPos)
        }
        // integer mode: strip decimal input
        if (integer) processedRaw = processedRaw.replace(/,.*$/, '')
        const formatted = formatWhileTyping(processedRaw)
        setDisplayText(formatted)
        prevDisplayRef.current = formatted
        const cleaned = formatted.replace(/\./g, '').replace(',', '.')
        const num = parseFloat(cleaned)
        // Only clamp to max while typing: intermediate values are often below min (e.g. typing "1" of "15000").
        // Min is enforced on blur.
        const clampedNum = isNaN(num) ? undefined : Math.min(max ?? Infinity, num)
        if (clampedNum !== undefined && clampedNum !== num) {
            const clampedFormatted = fmt(clampedNum)
            setDisplayText(clampedFormatted)
            prevDisplayRef.current = clampedFormatted
            setValue(clampedNum, true)
            requestAnimationFrame(() => inputRef.current?.setSelectionRange(clampedFormatted.length, clampedFormatted.length))
            return
        }
        setValue(clampedNum, true)
        const newCursorPos = getNewCursorPos(processedRaw, formatted, cursorPos)
        requestAnimationFrame(() => inputRef.current?.setSelectionRange(newCursorPos, newCursorPos))
    }

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault()
        let pasted = e.clipboardData.getData('text')
        if (!pasted.includes(',') && pasted.includes('.')) {
            const lastDot = pasted.lastIndexOf('.')
            const afterLastDot = pasted.substring(lastDot + 1).replace(/[^0-9]/g, '')
            // ≤2 digits after last dot → decimal separator; otherwise thousands separator
            pasted = afterLastDot.length <= 2
                ? pasted.substring(0, lastDot).replace(/\./g, '') + ',' + afterLastDot
                : pasted.replace(/\./g, '')
        }
        if (integer) pasted = pasted.replace(/,.*$/, '')
        const formatted = formatWhileTyping(pasted)
        setDisplayText(formatted)
        prevDisplayRef.current = formatted
        const cleaned = formatted.replace(/\./g, '').replace(',', '.')
        const num = parseFloat(cleaned)
        setValue(isNaN(num) ? undefined : Math.max(min ?? -Infinity, Math.min(max ?? Infinity, num)), true)
    }

    const handleWheel = (e: WheelEvent) => e.preventDefault()
    const handleFocus = () => inputRef.current?.addEventListener('wheel', handleWheel, { passive: false })
    const handleBlur = () => inputRef.current?.removeEventListener('wheel', handleWheel)

    const handleTextFocus = () => {
        isFocusedRef.current = true
        inputRef.current?.addEventListener('wheel', handleWheel, { passive: false })
    }

    const handleTextBlur = () => {
        isFocusedRef.current = false
        if (isDefined(currentValueRef.current) && isDefined(min) && currentValueRef.current! < min!) {
            setValue(min, true)
        }
        const formatted = isDefined(currentValueRef.current) ? fmt(currentValueRef.current!) : ''
        setDisplayText(formatted)
        prevDisplayRef.current = formatted
        inputRef.current?.removeEventListener('wheel', handleWheel)
    }

    const inputValue = isDefined(stateValue) ? roundFn(stateValue!) : ''

    if (thousandsSeparator) {
        return (
            <div className={fit ? styles.container : ''}>
                <input
                    id={id}
                    key={inputKey}
                    ref={inputRef}
                    type='text'
                    inputMode='decimal'
                    name={id}
                    value={displayText}
                    required={!optional}
                    className={`form-control ${className || ''} ${styles.number}`}
                    autoComplete="off"
                    onChange={handleTextChange}
                    onPaste={handlePaste}
                    onFocus={handleTextFocus}
                    onBlur={handleTextBlur}
                    {...rest}
                />
            </div>
        )
    }

    return (
        <div className={fit ? styles.container : ''}>
            <input
                id={id}
                key={inputKey}
                ref={inputRef}
                type='number'
                name={id}
                value={inputValue}
                min={roundDown(min)}
                max={roundUp(max)}
                step={integer ? '1' : (round ? '0.01' : 'any')}
                required={!optional}
                className={`form-control ${className || ''} ${styles.number}`}
                autoComplete="off"
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                {...rest}
            />
        </div>
    )
}
